/**
 * WebRTCService — manages peer connection, media, data channels, signaling.
 * Modeled exactly on the reference implementation's proven flow.
 *
 * Single start() method does everything in order:
 * 1. getUserMedia
 * 2. Create RTCPeerConnection
 * 3. Add local tracks
 * 4. Create data channels (offerer) or wait for them (answerer)
 * 5. Connect signaling + exchange SDP/ICE
 */

import { SignalingClient, type SignalingMessage } from './signalingClient.js';
import { createQr20WebrtcChannel, detectQr20WebrtcChannel } from '@tokenstreaming/transport-webrtc';
import { DEFAULT_ICE_SERVERS } from '@tokenstreaming/shared';

export interface WebRTCCallbacks {
  onRemoteStream: (stream: MediaStream) => void;
  onDataChannelReady: (channel: RTCDataChannel) => void;
  onConnected: () => void;
  onDisconnected: (reason: string) => void;
}

export class WebRTCService {
  private pc: RTCPeerConnection | null = null;
  private signaling: SignalingClient | null = null;
  private localStream: MediaStream | null = null;
  private callbacks: WebRTCCallbacks;
  private isOfferer: boolean;
  private facingMode: 'user' | 'environment' = 'user';

  constructor(callbacks: WebRTCCallbacks, isOfferer: boolean) {
    this.callbacks = callbacks;
    this.isOfferer = isOfferer;
  }

  async start(signalingUrl: string, roomId: string, secret: string): Promise<MediaStream | null> {
    // Step 1: Get local media (non-blocking — proceed without if denied)
    console.log('[webrtc] Requesting local media...');
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.facingMode, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true,
      });
      console.log('[webrtc] Local media acquired');
    } catch (err) {
      console.warn('[webrtc] Media access denied, proceeding without video:', err);
      this.localStream = null;
    }

    // Step 2: Create RTCPeerConnection
    this.pc = new RTCPeerConnection({
      iceServers: DEFAULT_ICE_SERVERS.map(s => ({ urls: s.urls })),
    });

    // Step 3: Add local tracks if we have them
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        this.pc.addTrack(track, this.localStream);
      }
    }

    // Handle remote tracks
    this.pc.ontrack = (event) => {
      console.log('[webrtc] Remote track received');
      if (event.streams[0]) {
        this.callbacks.onRemoteStream(event.streams[0]);
      }
    };

    // Monitor connection state
    this.pc.onconnectionstatechange = () => {
      console.log('[webrtc] Connection state:', this.pc?.connectionState);
      if (this.pc?.connectionState === 'connected') {
        this.callbacks.onConnected();
      } else if (
        this.pc?.connectionState === 'disconnected' ||
        this.pc?.connectionState === 'failed' ||
        this.pc?.connectionState === 'closed'
      ) {
        this.callbacks.onDisconnected(this.pc.connectionState);
      }
    };

    // Step 4: Data channels
    if (this.isOfferer) {
      // Offerer creates the qr20 data channel
      const qr20Channel = createQr20WebrtcChannel(this.pc);
      qr20Channel.binaryType = 'arraybuffer';
      qr20Channel.onopen = () => {
        console.log('[webrtc] qr20 data channel open');
        this.callbacks.onDataChannelReady(qr20Channel);
      };
    } else {
      // Answerer waits for data channels
      this.pc.ondatachannel = (event) => {
        console.log('[webrtc] Received data channel:', event.channel.label);
        if (detectQr20WebrtcChannel(event.channel)) {
          event.channel.binaryType = 'arraybuffer';
          if (event.channel.readyState === 'open') {
            this.callbacks.onDataChannelReady(event.channel);
          } else {
            event.channel.onopen = () => {
              this.callbacks.onDataChannelReady(event.channel);
            };
          }
        }
      };
    }

    // Step 5: Connect signaling and exchange SDP/ICE
    await this.connectSignaling(signalingUrl, roomId, secret);

    return this.localStream;
  }

  private async connectSignaling(signalingUrl: string, roomId: string, secret: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.signaling = new SignalingClient({
        onOpen: () => {
          console.log('[webrtc] Signaling connected, isOfferer:', this.isOfferer);
          if (!this.isOfferer) {
            // Answerer announces readiness immediately
            console.log('[webrtc] Sending peer-ready');
            this.signaling?.send({ type: 'peer-ready', payload: {} });
          }
          resolve();
        },
        onMessage: (msg) => {
          void this.handleSignalingMessage(msg);
        },
        onClose: (code, reason) => {
          console.log(`[webrtc] Signaling closed: ${code} ${reason}`);
        },
        onError: () => {
          console.error('[webrtc] Signaling error');
        },
      });

      // ICE candidate handler
      if (this.pc) {
        this.pc.onicecandidate = (event) => {
          if (event.candidate && this.signaling) {
            this.signaling.send({
              type: 'ice-candidate',
              payload: event.candidate.toJSON(),
            });
          }
        };
      }

      this.signaling.connect(signalingUrl, roomId, secret);
    });
  }

  private async createAndSendOffer(): Promise<void> {
    if (!this.pc || !this.signaling) return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.signaling.send({ type: 'sdp-offer', payload: offer });
    console.log('[webrtc] Offer sent');
  }

  private async handleSignalingMessage(msg: SignalingMessage): Promise<void> {
    if (!this.pc) return;

    if (msg.type === 'peer-joined') {
      // Server notifies both peers when the second participant joins.
      // In the QR flow the answerer always connects first, so its
      // initial peer-ready was dropped (no peer to relay to).
      if (this.isOfferer) {
        // Offerer just arrived — peer is already waiting, create offer.
        console.log('[webrtc] Peer joined, sending offer now');
        await this.createAndSendOffer();
      } else {
        // Answerer re-sends peer-ready now that offerer is present.
        console.log('[webrtc] Peer joined, re-sending peer-ready');
        this.signaling?.send({ type: 'peer-ready', payload: {} });
      }
      return;
    }

    if (msg.type === 'peer-ready') {
      if (this.isOfferer) {
        console.log('[webrtc] Peer ready, sending offer now');
        await this.createAndSendOffer();
      }
      return;
    }

    if (msg.type === 'sdp-offer') {
      console.log('[webrtc] Received SDP offer');
      const offer = msg.payload as RTCSessionDescriptionInit;
      await this.pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      this.signaling?.send({ type: 'sdp-answer', payload: answer });
      console.log('[webrtc] Answer sent');
    } else if (msg.type === 'sdp-answer') {
      console.log('[webrtc] Received SDP answer');
      const answer = msg.payload as RTCSessionDescriptionInit;
      await this.pc.setRemoteDescription(new RTCSessionDescription(answer));
    } else if (msg.type === 'ice-candidate') {
      const candidate = msg.payload as RTCIceCandidateInit;
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }

  toggleAudio(enabled: boolean): void {
    this.localStream?.getAudioTracks().forEach(t => { t.enabled = enabled; });
  }

  toggleVideo(enabled: boolean): void {
    this.localStream?.getVideoTracks().forEach(t => { t.enabled = enabled; });
  }

  /**
   * Camera-flip — swap front/rear video track without renegotiating SDP.
   * Uses RTCRtpSender.replaceTrack so the remote peer keeps the same m-line.
   * Mutates the existing localStream MediaStream in place so consumers
   * holding the reference see the new track.
   */
  async switchCamera(): Promise<void> {
    if (!this.pc || !this.localStream) return;
    const nextMode: 'user' | 'environment' = this.facingMode === 'user' ? 'environment' : 'user';
    let newStream: MediaStream;
    try {
      newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: nextMode, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false, // keep the existing audio track
      });
    } catch (err) {
      console.warn('[webrtc] switchCamera: getUserMedia failed:', err);
      return;
    }
    const newTrack = newStream.getVideoTracks()[0];
    if (!newTrack) {
      console.warn('[webrtc] switchCamera: no video track in new stream');
      return;
    }
    // Replace on the RTCPeerConnection sender (no SDP renegotiation needed)
    const sender = this.pc.getSenders().find(s => s.track?.kind === 'video');
    if (sender) {
      try {
        await sender.replaceTrack(newTrack);
      } catch (err) {
        console.warn('[webrtc] switchCamera: replaceTrack failed:', err);
        newTrack.stop();
        return;
      }
    }
    // Swap on the local MediaStream so the local <video> tile updates
    const oldTrack = this.localStream.getVideoTracks()[0];
    if (oldTrack) {
      this.localStream.removeTrack(oldTrack);
      oldTrack.stop();
    }
    this.localStream.addTrack(newTrack);
    this.facingMode = nextMode;
    console.log('[webrtc] Camera flipped to', nextMode);
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Graceful close — alias for stop(). Both are idempotent and CallPage
   * cleanup() invokes them sequentially as belt-and-braces teardown.
   */
  close(): void {
    this.stop();
  }

  stop(): void {
    console.log('[webrtc] Stopping...');
    this.localStream?.getTracks().forEach(t => t.stop());
    this.localStream = null;
    this.pc?.close();
    this.pc = null;
    this.signaling?.disconnect();
    this.signaling = null;
    console.log('[webrtc] Stopped');
  }
}
